import React, { useEffect, useMemo, useRef, useState } from "react";
import { midiToKey, playNote, playChord, audioSupported } from "../../lib/keyboardPlan";
import { chordWalk } from "../../lib/numberSenseV2";
import { Icon, Keys, Piano, TypingHint, MidiBadge, useTypingPiano } from "../numberSenseUi";
import useMidi from "../numberSense/useMidi";

const CAN_PLAY = audioSupported();

/* ============================================================
   Number Sense v2 — the guided chord walk, WalkScreen's chord
   twin: build every scale degree's triad on the piano yourself,
   one tone at a time (root → skip a note → 3rd → skip → 5th),
   and watch the quality (major/minor/diminished/augmented) fall
   out of the stacking instead of being told upfront. Runs from
   the drill menu for any triad-bearing scale type.

   Octave-exact like the scale walk: the 3rd and 5th are "up from
   the root you just placed", not just any pitch class. Two misses
   on a tone lights up the exact key.

   Each round finishes with a brief reveal (the chord's symbol +
   quality) before a Continue tap moves to the next degree. After
   the seventh round the finish screen auto-plays the whole set as
   a sequence of triads, then hands off to the drills via onDone.
   ============================================================ */

const keyId = (midi) => {
  const k = midiToKey(midi);
  return `${k.name}${k.octave}`;
};

// The auto-play spacing between triads on the finish screen (seconds).
const FINISH_BEAT_SEC = 0.55;

const ChordWalkScreen = ({ type, keyName, onDone, onClose }) => {
  const walk = useMemo(() => chordWalk(type.id, keyName), [type.id, keyName]);
  const rounds = walk.rounds;

  const [round, setRound] = useState(0);
  const [tone, setTone] = useState(0); // next tone to press: 0 root, 1 third, 2 fifth
  const [misses, setMisses] = useState(0);
  const [wrong, setWrong] = useState(false);
  const [revealed, setRevealed] = useState(false); // triad complete, showing quality before Continue

  const finished = round >= rounds.length;
  const current = finished ? null : rounds[round];

  // Keys lit on the piano: the tones already placed this round, all three
  // held over through the reveal so the shape stays visible while it's named.
  const placed = useMemo(() => {
    if (finished) return new Set();
    const n = revealed ? current.tones.length : tone;
    return new Set(current.tones.slice(0, n).map((t) => keyId(t.midi)));
  }, [current, tone, revealed, finished]);

  const playedFinish = useRef(false);

  // The finale: once every round is stacked, hear the whole set in order —
  // guarded so it only fires the moment the finish screen is entered.
  useEffect(() => {
    if (finished && CAN_PLAY && !playedFinish.current) {
      playedFinish.current = true;
      rounds.forEach((r, i) =>
        playChord(r.tones.map((t) => t.name), 500, i * FINISH_BEAT_SEC)
      );
    }
  }, [finished, rounds]);

  const press = (k) => {
    if (finished || revealed) return;
    const expected = current.tones[tone];
    if (k.midi === expected.midi) {
      setWrong(false);
      setMisses(0);
      if (tone === current.tones.length - 1) {
        // root, 3rd, and 5th are all down — reveal what just got built
        setRevealed(true);
        if (CAN_PLAY) {
          window.setTimeout(() => playChord(current.tones.map((t) => t.name)), 250);
        }
      } else {
        setTone((t) => t + 1);
      }
    } else {
      setWrong(true);
      setMisses((m) => m + 1);
    }
  };

  const advance = () => {
    setRound((r) => r + 1);
    setTone(0);
    setMisses(0);
    setWrong(false);
    setRevealed(false);
  };

  const resetAll = () => {
    playedFinish.current = false;
    setRound(0);
    setTone(0);
    setMisses(0);
    setWrong(false);
    setRevealed(false);
  };

  // Typing keyboard and MIDI feed the same walk (Piano taps sound themselves;
  // typing needs the note played here). Both go quiet during the reveal pause
  // and once the whole thing is stacked.
  const active = !finished && !revealed;
  const midi = useMidi(press, active);
  useTypingPiano((k) => {
    playNote(k.midi);
    press(k);
  }, active);

  return (
    <div className="ns-root">
      <div className="ns-app">
        <div className="subbar">
          <button className="iconbtn" onClick={onClose} aria-label="back">
            <Icon name="back" />
          </button>
          <div className="subbar-title">stack: {walk.label}</div>
        </div>
        <div className="scroll">
          {/* the chord set filling in, one triad per round */}
          <div className="teach-chips v2-walk-chips">
            {rounds.map((r, i) => {
              const completed = i < round;
              return (
                <span
                  className={`teach-chip ${completed ? "" : "v2-chip-dim"}`}
                  key={`${r.roman}-${i}`}
                >
                  <b className="mono">{r.roman}</b>
                  <span>{completed ? r.symbol : "?"}</span>
                </span>
              );
            })}
          </div>

          {!finished ? (
            <>
              <div className="v2-walk-banner">
                {!revealed ? (
                  <>
                    <div className="qkind">
                      chord {round + 1} of {rounds.length}
                    </div>
                    {tone === 0 && (
                      <div className="qprompt">
                        the <b className="mono">{current.degree}</b> chord —
                        start on <b>{current.tones[0].name}</b>, degree{" "}
                        <b className="mono">{current.degree}</b> of the scale
                      </div>
                    )}
                    {tone === 1 && (
                      <div className="qprompt">
                        from <b>{current.tones[0].name}</b>, skip one scale
                        note up — add the 3rd
                      </div>
                    )}
                    {tone === 2 && (
                      <div className="qprompt">skip another — add the 5th</div>
                    )}
                    {tone === 1 && (
                      <div className="qprompt-sub muted">
                        count two scale steps up from the root
                      </div>
                    )}
                    {tone === 2 && (
                      <div className="qprompt-sub muted">two more scale steps up</div>
                    )}
                    {wrong && (
                      <div className="v2-walk-msg">
                        not quite — count up the scale itself, not the keyboard
                        {misses >= 2 ? " (it's glowing now)" : ""}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="qkind">that's the chord</div>
                    <div className="qprompt">
                      <b>{current.symbol}</b> — {current.quality}, that's{" "}
                      {current.roman}
                    </div>
                    <button className="btn" onClick={advance}>
                      Continue
                    </button>
                  </>
                )}
              </div>
              <Piano
                pressed={new Set()}
                exactKeys={placed}
                exactHint={
                  !revealed && misses >= 2
                    ? new Set([keyId(current.tones[tone].midi)])
                    : null
                }
                onPress={press}
              />
              <TypingHint midiStatus={midi.status} />
              <MidiBadge status={midi.status} devices={midi.devices} />
            </>
          ) : (
            <>
              <div className="v2-walk-banner">
                <div className="qkind">all seven stacked</div>
                <div className="qprompt">
                  you just built every chord in <b>{walk.label}</b>
                </div>
                <div className="qprompt-sub muted">
                  same stacking, every key — the qualities never move
                </div>
              </div>
              <div className="v2-walk-actions">
                <button className="btn" onClick={onDone}>
                  Drill the chords
                </button>
                <button className="btn ghost" onClick={resetAll}>
                  Stack them again
                </button>
              </div>
            </>
          )}

          <div className="footnote">
            <Keys />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChordWalkScreen;
